import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  Sync,
  InfoOutlined,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiService, BotStatus } from '../services/api';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STATUS_CONFIG: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'default'; icon: React.ReactNode }> = {
  SUCESSO: { label: 'Atualizado', color: 'success', icon: <CheckCircleOutline fontSize="small" /> },
  SUCESSO_COM_ALERTAS: { label: 'Com alertas', color: 'warning', icon: <CheckCircleOutline fontSize="small" /> },
  ERRO: { label: 'Erro', color: 'error', icon: <ErrorOutline fontSize="small" /> },
  TIMEOUT: { label: 'Timeout', color: 'error', icon: <ErrorOutline fontSize="small" /> },
  PAINEL_INDISPONIVEL: { label: 'Painel indisponível', color: 'error', icon: <ErrorOutline fontSize="small" /> },
  INICIADO: { label: 'Em execução…', color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
  em_execucao: { label: 'Em execução…', color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
  not_configured: { label: 'Não configurado', color: 'default', icon: <InfoOutlined fontSize="small" /> },
};

function _fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return '—';
  }
}

function _ago(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return '';
  }
}

interface Props {
  compact?: boolean;
}

export function LegislacaoSyncStatus({ compact = false }: Props) {
  const { data, isLoading, refetch, isFetching } = useQuery<BotStatus>(
    ['bot-status'],
    () => apiService.getBotStatus(),
    {
      refetchInterval: 60_000,
      retry: 1,
    }
  );

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">Verificando coleta…</Typography>
      </Box>
    );
  }

  const lastExec = data?.ultima_execucao;
  const apontamento = data?.apontamento;
  const statusKey = lastExec?.status ?? apontamento?.status ?? 'not_configured';
  const cfg = STATUS_CONFIG[statusKey] ?? STATUS_CONFIG['not_configured'];

  if (compact) {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" display="block">
              Última coleta MMA: {_fmt(lastExec?.inicio)}
            </Typography>
            {lastExec && (
              <Typography variant="caption" display="block">
                +{lastExec.novos} novos · ✏ {lastExec.atualizados} atualizados
                {lastExec.erros > 0 && ` · ⚠ ${lastExec.erros} erros`}
              </Typography>
            )}
            <Typography variant="caption" display="block">
              Próxima: {_fmt(apontamento?.proxima_execucao)}
            </Typography>
          </Box>
        }
        arrow
      >
        <Chip
          icon={cfg.icon as React.ReactElement}
          label={cfg.label}
          color={cfg.color}
          size="small"
          variant="outlined"
          sx={{ cursor: 'default', fontWeight: 500 }}
        />
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        flexWrap: 'wrap',
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Chip
          icon={cfg.icon as React.ReactElement}
          label={cfg.label}
          color={cfg.color}
          size="small"
          variant="filled"
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Bot MMA
        </Typography>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Última coleta
        </Typography>
        <Tooltip title={_ago(lastExec?.inicio)}>
          <Typography variant="body2" fontWeight={500}>
            {_fmt(lastExec?.inicio)}
          </Typography>
        </Tooltip>
      </Box>

      {lastExec && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Resultado
          </Typography>
          <Typography variant="body2">
            +{lastExec.novos} novos &nbsp;·&nbsp; ✏&nbsp;{lastExec.atualizados} atualizados
            {lastExec.erros > 0 && (
              <Typography component="span" color="error.main">
                &nbsp;·&nbsp; ⚠ {lastExec.erros} erros
              </Typography>
            )}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Próxima execução
        </Typography>
        <Typography variant="body2">
          {_fmt(apontamento?.proxima_execucao) || 'Automática às 02:00'}
        </Typography>
      </Box>

      {apontamento?.lock_execucao && (
        <Chip label="Em execução agora" color="warning" size="small" icon={<HourglassEmpty fontSize="small" />} />
      )}

      <Tooltip title="Atualizar status">
        <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
          <Sync fontSize="small" sx={{ animation: isFetching ? 'spin 1s linear infinite' : 'none' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
